/* eslint-disable */
"use client";
import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { DealBookingModal, type DealBookingFormData } from '../deal-booking-modal';
import { useMapboxMap } from './useMapboxMap';
import { useMapInteractions } from './useMapInteractions';
import { useMapMarkers } from './useMapMarkers';
import { useRadiusLayers } from './useRadiusLayers';
import { useMapTooltip } from './mapTooltip';
import type { RotationMapProps } from './types';
import 'mapbox-gl/dist/mapbox-gl.css';

// Expect NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN to be set in environment
mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN || '';

export const RotationMap: React.FC<RotationMapProps> = ({
  preceptors,
  height = 300,
  zoom = 8,
  markerColor = '#dc2626',
  onMarkerClick,
  losAngelesOnly = true,
  showPhotos = false,
  photoSize = 40,
  zip,
  showZipPin = false,
  extraPins = [],
  pinStyle = 'badge',
  lockZoom = false,
  fixedZoomLevel,
  autoFit = false,
  detailView = false,
  freezeZoom = false,
  disablePan = false,
  restrictZoomOut = false,
  userLocation,
  centerOverride,
  showIndices = false,
  zipBounds,
  zipFitPadding = 40,
  selectedId,
  fullHeight = false,
  disableUserZoom = false,
  clusterCentroidMode = false,
  clusterLabel,
  showTooltips = true,
  radiusMiles,
  onBoundsChange,
  fitBoundsKey,
}) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPreceptor, setSelectedPreceptor] = useState<typeof preceptors[number] | null>(null);

  const { mapRef, mapLoaded, currentZoom, userInteractedRef } = useMapboxMap({
    mapContainer,
    losAngelesOnly,
    centerOverride,
    userLocation,
    preceptors,
    zoom,
    detailView,
    lockZoom,
    fixedZoomLevel,
    onBoundsChange,
    autoFit,
  });

  useMapInteractions({
    mapRef,
    lockZoom,
    fixedZoomLevel,
    freezeZoom,
    disablePan,
    disableUserZoom,
    detailView,
    zoom,
    losAngelesOnly,
    preceptorsLength: preceptors.length,
  });

  const { removeRadiusLayers, renderRadiusLayers } = useRadiusLayers(mapRef);
  const { showTooltipForPreceptor, scheduleHideTooltip, clearTooltip, popupRef } = useMapTooltip(mapRef, showTooltips);

  useMapMarkers({
    mapRef,
    mapLoaded,
    preceptors,
    selectedId,
    showPhotos,
    photoSize,
    pinStyle,
    markerColor,
    showIndices,
    radiusMiles,
    currentZoom,
    clusterCentroidMode,
    clusterLabel,
    losAngelesOnly,
    centerOverride,
    zipBounds,
    userInteractedRef,
    autoFit,
    detailView,
    freezeZoom,
    fixedZoomLevel,
    disablePan,
    userLocation,
    extraPins,
    onMarkerClick,
    showTooltipForPreceptor,
    scheduleHideTooltip,
    removeRadiusLayers,
    renderRadiusLayers,
  });

  const lastFitKey = useRef<string>('');
  const lastFitBoundsKey = useRef<number | undefined>(undefined);
  
  useEffect(() => {
    if (!mapRef.current || (!centerOverride && !zipBounds)) return;
    const map = mapRef.current;

    const keyBase = zip ? `zip:${zip}` : 'zip:none';
    const key = zipBounds
      ? `${keyBase}:bbox:${zipBounds.join(',')}:fk:${fitBoundsKey ?? 0}`
      : `${keyBase}:pt:${centerOverride![0].toFixed(6)},${centerOverride![1].toFixed(6)}:fk:${fitBoundsKey ?? 0}`;

    // Reset user interaction flag when fitBoundsKey changes (filter re-selected)
    if (fitBoundsKey !== undefined && fitBoundsKey !== lastFitBoundsKey.current) {
      userInteractedRef.current = false;
      lastFitBoundsKey.current = fitBoundsKey;
    }

    if (userInteractedRef.current && key === lastFitKey.current) return;

    if (key === lastFitKey.current) {
      map.resize();
      return;
    }
    lastFitKey.current = key;

    if (zipBounds) {
      const [minLng, minLat, maxLng, maxLat] = zipBounds;
      map.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
        padding: zipFitPadding,
        maxZoom: detailView ? 15 : 14,
        duration: 500,
        linear: true,
      });
    } else if (centerOverride) {
      map.flyTo({ center: centerOverride, zoom: detailView ? 15 : 13, duration: 500, essential: true });
    }
  }, [centerOverride, zipBounds, zipFitPadding, detailView, zip, mapRef, userInteractedRef, fitBoundsKey]);

  useEffect(() => {
    return () => {
      clearTooltip();
      removeRadiusLayers();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    };
  }, [clearTooltip, removeRadiusLayers, mapRef, popupRef]);

  const containerHeight = fullHeight ? '100%' : height;

  return (
    <div className="w-full relative" style={{ height: containerHeight }}>
      {!process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN && (
        <div className="text-[11px] text-center text-red-600 bg-red-50 p-2 rounded mb-2">
          Mapbox token missing. Set NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN in .env.local.
        </div>
      )}
      <div className="w-full h-full rounded-md overflow-hidden">
        <div ref={mapContainer} className="w-full h-full cursor-grab active:cursor-grabbing" />
      </div>
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-neutral-100 to-neutral-200 animate-pulse">
          <div className="flex flex-col items-center gap-2">
            <div className="w-10 h-10 border-4 border-red-600/30 border-t-red-600 rounded-full animate-spin" />
            <span className="text-xs text-neutral-600">Loading map…</span>
          </div>
        </div>
      )}

      <DealBookingModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPreceptor(null);
        }}
        preceptorName={selectedPreceptor?.name}
        preceptorSpecialty={selectedPreceptor?.specialty}
        preceptorCity={selectedPreceptor?.city}
        onSubmit={async (formData: DealBookingFormData) => {
          console.log('📧 Message submitted:', {
            preceptor: selectedPreceptor?.name,
            formData,
          });
          try {
            const response = await fetch('/api/preceptor-messages', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                preceptor_id: selectedPreceptor?.id,
                preceptor_name: selectedPreceptor?.name,
                ...formData,
              }),
            });
            if (!response.ok) {
              console.error('Failed to send message');
            }
          } catch (err) {
            console.error('Error sending message:', err);
          }
        }}
      />
    </div>
  );
};

export default RotationMap;
